import { Injectable } from "@angular/core";
import { environment } from "@environment";
import { HttpClient } from "@angular/common/http";

import { TimeoutError } from "rxjs";

import { UtilityService } from "@core/services/util/utility.service";
import { LoginService } from "@core/services/login/login.service";
import { GtmService } from "@core/services/analytics/gtm.service";
import { StorageService } from "@core/services/util/storage.service";

export type GoogleMapQueryApi = "textQuery" | "latLngQuery" | "placeIdQuery";

@Injectable({
    providedIn: "root"
})
export class LocationApiService {
    cache$: any = {};
    queryQueue: Array<object> = [];
    queryInProgress: any = {};

    constructor(
        private http: HttpClient,
        private utilityService: UtilityService,
        private loginService: LoginService,
        private gtmService: GtmService,
        private storageService: StorageService
    ) { }

    getLatestQuery(queryType: GoogleMapQueryApi): object {
        const index = this.queryQueue
            .map((query: any) => query.type)
            .lastIndexOf(queryType);

        const query = index >= 0 ? this.queryQueue[index] : null;
        this.queryQueue = this.queryQueue.filter(
            (query: any) => query.type !== queryType
        );
        return query;
    }

    nextQuery(queryType: GoogleMapQueryApi) {
        const latestQuery = this.getLatestQuery(queryType);
        return latestQuery;
    }

    makeQuery(resouceUrl: string, params: any, cacheKey: string, queryType: GoogleMapQueryApi, queryVersion: string = '') {
        if (this.queryInProgress[cacheKey]) {
            this.queryQueue.push({
                type: cacheKey,
                params
            });

            return Promise.resolve({ waiting: true });
        }


        if (this.cache$[cacheKey]) {
            this.gtmService.logWebAnalyticsEvent('address', 'Response Served From Cache, Query Type: ' + queryType);
            return Promise.resolve(this.cache$[cacheKey]);
        }

        this.queryInProgress[queryType] = true;

        return new Promise((resolve, reject) => {
            this.http.get(environment.apiUrl + `${queryVersion}/customer/${resouceUrl}`, {
                    params,
                    headers: this.getAuthHeaders(queryVersion)
                })
                .toPromise()
                .then((resp: any) => {
                    if (resp && resp.meta.code === 200) {
                        this.cache$[cacheKey] = resp;
                        resolve(resp);
                    }
                    this.queryInProgress[queryType] = false;
                })
                .catch((e: any) => {
                    if (e instanceof TimeoutError) {
                        const next_query: any = this.nextQuery(queryType);
                        if (next_query) {
                            resolve(this.makeQuery(resouceUrl, params, cacheKey, queryType));
                        }
                    }

                    this.queryInProgress[queryType] = false;
                    reject(e);
                });
        });
    }

    getAuthHeaders(queryVersion: string) {
        let headers: any = {
            ...this.utilityService.getAuthHeaders()
        };

        if (queryVersion.includes('v2')) {
            headers['Accept'] = 'application/vnd.phpl.v2'
        }

        return headers;
    }

    async getAutocompleteQuery(input: string) {
        await this.loginService.ensureSessionId(true);

        input = this._trimQueryText(input);
        const cacheKey = input;
        const queryType = 'textQuery';

        return this.makeQuery('get_autocomplete_query', { input }, cacheKey, queryType);
    }

    async reverseGeocodeUsingPlaceId(place_id: string, input: string, address_format: boolean = true) {
        await this.loginService.ensureSessionId(true);

        const agent_id = this.storageService.get('agent_id') || '';
        const cacheKey = `${place_id}_${input}_${agent_id}`;
        const queryType = 'placeIdQuery';

        return this.makeQuery('get_reverse_geocode', { place_id, input, address_format, agent_id }, cacheKey, queryType, '/v2');
    }

    async reverseGeocodeUsingLatLng(lat: number, lng: number, address_format: boolean = true) {
        await this.loginService.ensureSessionId(true);

        const agent_id = this.storageService.get('agent_id') || '';
        const cacheKey = `${String(lat)}_${String(lng)}_${agent_id}`;
        const queryType = 'latLngQuery';

        return this.makeQuery('get_reverse_geocode', { lat, lng, address_format, agent_id }, cacheKey, queryType, '/v2');
    }

    reverseGeocode(data: any) {
        const { lat, lng, place_id, input } = data;

        if (lat && lng) {
            return this.reverseGeocodeUsingLatLng(lat, lng);
        }

        if (place_id && input) {
            return this.reverseGeocodeUsingPlaceId(place_id, input);
        }

        return Promise.reject(new Error('Invalid input data'));
    }

    private _trimQueryText(queryText: string) {
        return queryText.trim();
    }
}
